// Decrypt Orenda's secure-card-details response.
//
// Spec (confirmed by Christian 2026-10-21):
//
//   const key = forge.util.createBuffer(secretKey, "utf8");  // raw string bytes
//   const iv  = base64Decode(cipherText).substring(0, 16);
//   const ct  = base64Decode(cipherText).substring(16);
//   AES-CFB.decrypt(key, iv, ct) → UTF-8 plaintext
//
// Why we hand-roll AES instead of using Web Crypto:
//   - Orenda's secretKey decodes to 24 UTF-8 bytes → AES-192.
//   - Web Crypto spec deliberately omits AES-192 — browsers reject the
//     importKey call with "192-bit AES keys are not supported".
//   - Node-forge (what Orenda's sample uses) supports all three sizes.
//   - Rather than pulling in a dependency, we inline the AES core here.
//     ~150 lines, matches NIST FIPS-197 exactly.
//
// Only `pan` and `cvc2` are AES-CFB encrypted. `expiry` and `printedName`
// come back as plaintext (Christian confirmed).

// ─── base64 → bytes ────────────────────────────────────────────────

const b64ToBytes = (s) => {
  if (s == null) return new Uint8Array(0);
  let str = String(s).trim();
  if (!str) return new Uint8Array(0);
  str = str.replace(/=+$/, "").replace(/-/g, "+").replace(/_/g, "/");
  const pad = "=".repeat((4 - (str.length % 4)) % 4);
  const bin = atob(str + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

// ─── AES core (FIPS-197) ───────────────────────────────────────────
// AES encrypts a single 16-byte block. We only need block-encrypt (not
// decrypt) because CFB uses the forward cipher in both directions.

// S-box.
const SBOX = new Uint8Array([
  0x63,0x7c,0x77,0x7b,0xf2,0x6b,0x6f,0xc5,0x30,0x01,0x67,0x2b,0xfe,0xd7,0xab,0x76,
  0xca,0x82,0xc9,0x7d,0xfa,0x59,0x47,0xf0,0xad,0xd4,0xa2,0xaf,0x9c,0xa4,0x72,0xc0,
  0xb7,0xfd,0x93,0x26,0x36,0x3f,0xf7,0xcc,0x34,0xa5,0xe5,0xf1,0x71,0xd8,0x31,0x15,
  0x04,0xc7,0x23,0xc3,0x18,0x96,0x05,0x9a,0x07,0x12,0x80,0xe2,0xeb,0x27,0xb2,0x75,
  0x09,0x83,0x2c,0x1a,0x1b,0x6e,0x5a,0xa0,0x52,0x3b,0xd6,0xb3,0x29,0xe3,0x2f,0x84,
  0x53,0xd1,0x00,0xed,0x20,0xfc,0xb1,0x5b,0x6a,0xcb,0xbe,0x39,0x4a,0x4c,0x58,0xcf,
  0xd0,0xef,0xaa,0xfb,0x43,0x4d,0x33,0x85,0x45,0xf9,0x02,0x7f,0x50,0x3c,0x9f,0xa8,
  0x51,0xa3,0x40,0x8f,0x92,0x9d,0x38,0xf5,0xbc,0xb6,0xda,0x21,0x10,0xff,0xf3,0xd2,
  0xcd,0x0c,0x13,0xec,0x5f,0x97,0x44,0x17,0xc4,0xa7,0x7e,0x3d,0x64,0x5d,0x19,0x73,
  0x60,0x81,0x4f,0xdc,0x22,0x2a,0x90,0x88,0x46,0xee,0xb8,0x14,0xde,0x5e,0x0b,0xdb,
  0xe0,0x32,0x3a,0x0a,0x49,0x06,0x24,0x5c,0xc2,0xd3,0xac,0x62,0x91,0x95,0xe4,0x79,
  0xe7,0xc8,0x37,0x6d,0x8d,0xd5,0x4e,0xa9,0x6c,0x56,0xf4,0xea,0x65,0x7a,0xae,0x08,
  0xba,0x78,0x25,0x2e,0x1c,0xa6,0xb4,0xc6,0xe8,0xdd,0x74,0x1f,0x4b,0xbd,0x8b,0x8a,
  0x70,0x3e,0xb5,0x66,0x48,0x03,0xf6,0x0e,0x61,0x35,0x57,0xb9,0x86,0xc1,0x1d,0x9e,
  0xe1,0xf8,0x98,0x11,0x69,0xd9,0x8e,0x94,0x9b,0x1e,0x87,0xe9,0xce,0x55,0x28,0xdf,
  0x8c,0xa1,0x89,0x0d,0xbf,0xe6,0x42,0x68,0x41,0x99,0x2d,0x0f,0xb0,0x54,0xbb,0x16,
]);

// GF(2^8) multiply by 2 (xtime).
const xtime = (b) => ((b << 1) ^ ((b & 0x80) ? 0x1b : 0)) & 0xff;

// AES round constants (Rcon[1..10]).
const RCON = [0x00,0x01,0x02,0x04,0x08,0x10,0x20,0x40,0x80,0x1b,0x36];

// Key expansion. Returns a Uint8Array of 16*(Nr+1) bytes.
// Nr = 10 (AES-128), 12 (AES-192), 14 (AES-256).
const keyExpansion = (keyBytes) => {
  const Nk = keyBytes.length / 4;       // 4, 6, 8
  const Nr = Nk + 6;                     // 10, 12, 14
  const total = 16 * (Nr + 1);           // in bytes
  const w = new Uint8Array(total);
  // First Nk words = the key.
  w.set(keyBytes, 0);
  // Expand.
  for (let i = Nk; i < 4 * (Nr + 1); i++) {
    let t0 = w[(i - 1) * 4 + 0];
    let t1 = w[(i - 1) * 4 + 1];
    let t2 = w[(i - 1) * 4 + 2];
    let t3 = w[(i - 1) * 4 + 3];
    if (i % Nk === 0) {
      // RotWord + SubWord + Rcon.
      const r = t0;
      t0 = SBOX[t1] ^ RCON[i / Nk];
      t1 = SBOX[t2];
      t2 = SBOX[t3];
      t3 = SBOX[r];
    } else if (Nk > 6 && i % Nk === 4) {
      // Extra SubWord for AES-256.
      t0 = SBOX[t0]; t1 = SBOX[t1]; t2 = SBOX[t2]; t3 = SBOX[t3];
    }
    const base = i * 4;
    const prevBase = (i - Nk) * 4;
    w[base + 0] = w[prevBase + 0] ^ t0;
    w[base + 1] = w[prevBase + 1] ^ t1;
    w[base + 2] = w[prevBase + 2] ^ t2;
    w[base + 3] = w[prevBase + 3] ^ t3;
  }
  return { w, Nr };
};

// Encrypt one 16-byte block in place using the expanded key schedule.
const encryptBlock = (block, w, Nr) => {
  const s = block; // operate in place
  // AddRoundKey (round 0).
  for (let i = 0; i < 16; i++) s[i] ^= w[i];
  for (let round = 1; round < Nr; round++) {
    // SubBytes + ShiftRows + MixColumns + AddRoundKey.
    const a = new Uint8Array(16);
    for (let i = 0; i < 16; i++) a[i] = SBOX[s[i]];
    // ShiftRows: row r shifted left by r.
    // Column-major state: s[c*4+r] is row r, column c.
    const t = new Uint8Array(16);
    t[0]  = a[0];  t[1]  = a[5];  t[2]  = a[10]; t[3]  = a[15];
    t[4]  = a[4];  t[5]  = a[9];  t[6]  = a[14]; t[7]  = a[3];
    t[8]  = a[8];  t[9]  = a[13]; t[10] = a[2];  t[11] = a[7];
    t[12] = a[12]; t[13] = a[1];  t[14] = a[6];  t[15] = a[11];
    // MixColumns per column.
    for (let c = 0; c < 4; c++) {
      const i0 = c * 4;
      const b0 = t[i0], b1 = t[i0 + 1], b2 = t[i0 + 2], b3 = t[i0 + 3];
      s[i0 + 0] = xtime(b0) ^ (xtime(b1) ^ b1) ^ b2 ^ b3;
      s[i0 + 1] = b0 ^ xtime(b1) ^ (xtime(b2) ^ b2) ^ b3;
      s[i0 + 2] = b0 ^ b1 ^ xtime(b2) ^ (xtime(b3) ^ b3);
      s[i0 + 3] = (xtime(b0) ^ b0) ^ b1 ^ b2 ^ xtime(b3);
    }
    // AddRoundKey.
    const rkBase = round * 16;
    for (let i = 0; i < 16; i++) s[i] ^= w[rkBase + i];
  }
  // Final round: SubBytes + ShiftRows + AddRoundKey (no MixColumns).
  const a = new Uint8Array(16);
  for (let i = 0; i < 16; i++) a[i] = SBOX[s[i]];
  s[0]  = a[0];  s[1]  = a[5];  s[2]  = a[10]; s[3]  = a[15];
  s[4]  = a[4];  s[5]  = a[9];  s[6]  = a[14]; s[7]  = a[3];
  s[8]  = a[8];  s[9]  = a[13]; s[10] = a[2];  s[11] = a[7];
  s[12] = a[12]; s[13] = a[1];  s[14] = a[6];  s[15] = a[11];
  const rkBase = Nr * 16;
  for (let i = 0; i < 16; i++) s[i] ^= w[rkBase + i];
};

// ─── AES-CFB128 decrypt ────────────────────────────────────────────

const AES_BLOCK = 16;

const aesCfbDecrypt = (keyBytes, iv, ciphertext) => {
  if (iv.length !== AES_BLOCK) {
    throw new Error(`AES-CFB IV must be ${AES_BLOCK} bytes, got ${iv.length}`);
  }
  if (keyBytes.length !== 16 && keyBytes.length !== 24 && keyBytes.length !== 32) {
    throw new Error(`AES key must be 16/24/32 bytes, got ${keyBytes.length}`);
  }
  const { w, Nr } = keyExpansion(keyBytes);
  const plaintext = new Uint8Array(ciphertext.length);
  let feedback = new Uint8Array(iv); // copy — encryptBlock mutates
  let offset = 0;
  while (offset < ciphertext.length) {
    // keystream block = AES_ECB(feedback)
    const keystream = new Uint8Array(feedback);
    encryptBlock(keystream, w, Nr);
    const chunk = Math.min(AES_BLOCK, ciphertext.length - offset);
    const nextFeedback = new Uint8Array(AES_BLOCK);
    for (let i = 0; i < chunk; i++) {
      const c = ciphertext[offset + i];
      plaintext[offset + i] = c ^ keystream[i];
      nextFeedback[i] = c; // CFB128 feeds back the ciphertext
    }
    feedback = nextFeedback;
    offset += chunk;
  }
  return plaintext;
};

const utf8 = (bytes) => new TextDecoder("utf-8", { fatal: false }).decode(bytes);

// ─── Public API ────────────────────────────────────────────────────

const decryptField = (secretKeyBytes, base64CipherText) => {
  if (!base64CipherText) return null;
  const fullBytes = b64ToBytes(base64CipherText);
  if (fullBytes.length <= AES_BLOCK) return null;
  const iv = fullBytes.slice(0, AES_BLOCK);
  const ct = fullBytes.slice(AES_BLOCK);
  return utf8(aesCfbDecrypt(secretKeyBytes, iv, ct));
};

// Decrypt the secure card details envelope.
//
// Input `details`:
//   { pan, cvc2, cvv, expiry, expiry_date, printedName, name, cardId }
// Only `pan` and `cvc2`/`cvv` are AES-CFB encrypted. `expiry`/`expiry_date`
// and `printedName` are plaintext — forwarded verbatim.
//
// Input `secretKey` is the string from the /secure-key response, used as a
// raw UTF-8 byte sequence for the AES key.
export const decryptSecureCardDetails = async (details, secretKey) => {
  if (!details) throw new Error("No card details to decrypt.");
  if (!secretKey) throw new Error("No secretKey to decrypt with.");

  const keyBytes = new TextEncoder().encode(String(secretKey));
  if (keyBytes.length !== 16 && keyBytes.length !== 24 && keyBytes.length !== 32) {
    throw new Error(
      `secretKey length ${keyBytes.length} is not a valid AES key length (16/24/32).`
    );
  }

  const panB64 = details.pan ?? null;
  const cvcB64 = details.cvc2 ?? details.cvv ?? null;
  const expiryRaw = details.expiry ?? details.expiry_date ?? null;

  const pan = decryptField(keyBytes, panB64);
  const cvc2 = decryptField(keyBytes, cvcB64);

  // Christian said expiry is plaintext — but the sandbox response actually
  // returns it as a base64 blob the same shape as the encrypted PAN/CVV
  // (IV + ciphertext). Try to decrypt whenever the raw value looks like
  // base64-of-more-than-one-block; accept anything printable that looks
  // like a date (digits, optional separator). Fall back to raw if decrypt
  // yields garbage or the raw value is clearly already plaintext.
  let expiry = expiryRaw;
  if (expiryRaw && /^[A-Za-z0-9+/=_-]+$/.test(expiryRaw)) {
    try {
      const decoded = b64ToBytes(expiryRaw);
      if (decoded.length > AES_BLOCK) {
        const decrypted = decryptField(keyBytes, expiryRaw);
        // Accept MMYY, MM/YY, MM-YY, MM/YYYY, and any short digits+slash
        // combination up to 8 chars. Anything with control chars or weird
        // bytes means wrong key / not actually encrypted; keep raw.
        if (decrypted && /^[\d/-]{3,10}$/.test(decrypted)) {
          expiry = decrypted;
        }
      }
    } catch { /* keep raw */ }
  }

  return {
    pan,
    cvc2,
    expiry,
    printedName: details.printedName ?? details.name ?? null,
    cardId: details.cardId ?? null,
  };
};
